/**
 * MIDI Engine for String Sauce
 * Handles MIDI events, synth playback, and instrument tracks
 */

// MIDI Note names
export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Convert MIDI note number to frequency
export const midiToFrequency = (note) => {
  return 440 * Math.pow(2, (note - 69) / 12);
};

// Convert MIDI note number to note name
export const midiToNoteName = (note) => {
  const octave = Math.floor(note / 12) - 1;
  const noteName = NOTE_NAMES[note % 12];
  return `${noteName}${octave}`;
};

// MIDI Event Types
export const MIDIEventType = {
  NOTE_ON: 'NOTE_ON',
  NOTE_OFF: 'NOTE_OFF',
};

// Create a MIDI event
export const createMIDIEvent = (type, note, velocity = 100, time = 0, channel = 1) => ({
  type,
  note,
  velocity: Math.max(0, Math.min(127, velocity)),
  time, // Audio clock time in seconds
  channel,
  id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
});

// Drum Kit Mapping (GM Standard)
export const DRUM_KIT = {
  kick: { note: 36, name: 'Kick', color: 'from-red-500 to-red-600' },
  snare: { note: 38, name: 'Snare', color: 'from-amber-500 to-amber-600' },
  hihatClosed: { note: 42, name: 'Hi-Hat Closed', color: 'from-cyan-500 to-cyan-600' },
  hihatOpen: { note: 46, name: 'Hi-Hat Open', color: 'from-cyan-400 to-cyan-500' },
  tom1: { note: 48, name: 'Tom 1', color: 'from-orange-500 to-orange-600' },
  tom2: { note: 45, name: 'Tom 2', color: 'from-orange-400 to-orange-500' },
  tom3: { note: 43, name: 'Floor Tom', color: 'from-orange-600 to-orange-700' },
  crash: { note: 49, name: 'Crash', color: 'from-yellow-500 to-yellow-600' },
  ride: { note: 51, name: 'Ride', color: 'from-yellow-400 to-yellow-500' },
  clap: { note: 39, name: 'Clap', color: 'from-pink-500 to-pink-600' },
  rimshot: { note: 37, name: 'Rimshot', color: 'from-purple-500 to-purple-600' },
  cowbell: { note: 56, name: 'Cowbell', color: 'from-emerald-500 to-emerald-600' },
  tambourine: { note: 54, name: 'Tambourine', color: 'from-lime-500 to-lime-600' },
  shaker: { note: 70, name: 'Shaker', color: 'from-teal-500 to-teal-600' },
  perc1: { note: 60, name: 'Perc 1', color: 'from-violet-500 to-violet-600' },
  perc2: { note: 61, name: 'Perc 2', color: 'from-indigo-500 to-indigo-600' },
};

// Standard pad layout (4x4 grid)
export const PAD_LAYOUT = [
  ['kick', 'snare', 'hihatClosed', 'hihatOpen'],
  ['tom1', 'tom2', 'tom3', 'crash'],
  ['clap', 'rimshot', 'ride', 'cowbell'],
  ['tambourine', 'shaker', 'perc1', 'perc2'],
];

// Computer keyboard to piano note mapping (QWERTY)
export const KEYBOARD_MAP = {
  // Lower octave (A-row)
  'a': 48, // C3
  'w': 49, // C#3
  's': 50, // D3
  'e': 51, // D#3
  'd': 52, // E3
  'f': 53, // F3
  't': 54, // F#3
  'g': 55, // G3
  'y': 56, // G#3
  'h': 57, // A3
  'u': 58, // A#3
  'j': 59, // B3
  'k': 60, // C4 (Middle C)
  'o': 61, // C#4
  'l': 62, // D4
  'p': 63, // D#4
  ';': 64, // E4
};

/**
 * Simple Synth Engine
 * Creates oscillator-based sounds for MIDI playback
 */
export class SimpleSynth {
  constructor(audioContext, destination) {
    this.audioContext = audioContext;
    this.destination = destination;
    this.activeNotes = new Map(); // note -> { osc, gain }
    this.type = 'sine'; // sine, square, sawtooth, triangle
    this.attack = 0.01;
    this.decay = 0.1;
    this.sustain = 0.7;
    this.release = 0.2;
  }

  setWaveform(type) {
    this.type = type;
  }

  noteOn(note, velocity = 100, time = null) {
    const ctx = this.audioContext;
    const now = time || ctx.currentTime;
    const freq = midiToFrequency(note);
    const vel = velocity / 127;

    // Create oscillator
    const osc = ctx.createOscillator();
    osc.type = this.type;
    osc.frequency.setValueAtTime(freq, now);

    // Create gain for ADSR envelope
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, now);
    
    // Attack
    gainNode.gain.linearRampToValueAtTime(vel, now + this.attack);
    // Decay to sustain
    gainNode.gain.linearRampToValueAtTime(vel * this.sustain, now + this.attack + this.decay);

    // Connect
    osc.connect(gainNode);
    gainNode.connect(this.destination);

    // Start
    osc.start(now);

    // Store active note
    this.activeNotes.set(note, { osc, gain: gainNode, velocity: vel });

    return { osc, gain: gainNode };
  }

  noteOff(note, time = null) {
    const ctx = this.audioContext;
    const now = time || ctx.currentTime;
    
    const active = this.activeNotes.get(note);
    if (!active) return;

    const { osc, gain } = active;

    // Release
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + this.release);

    // Stop and cleanup
    osc.stop(now + this.release + 0.1);
    
    setTimeout(() => {
      this.activeNotes.delete(note);
    }, (this.release + 0.2) * 1000);
  }

  stopAll() {
    const ctx = this.audioContext;
    const now = ctx.currentTime;
    
    this.activeNotes.forEach((active, note) => {
      active.osc.stop(now + 0.01);
    });
    this.activeNotes.clear();
  }
}

/**
 * Drum Synth Engine
 * Creates percussion sounds using synthesis
 */
export class DrumSynth {
  constructor(audioContext, destination) {
    this.audioContext = audioContext;
    this.destination = destination;
  }

  playDrum(drumName, velocity = 100, time = null) {
    const ctx = this.audioContext;
    const now = time || ctx.currentTime;
    const vel = velocity / 127;

    switch (drumName) {
      case 'kick':
        this.playKick(now, vel);
        break;
      case 'snare':
        this.playSnare(now, vel);
        break;
      case 'hihatClosed':
        this.playHiHat(now, vel, false);
        break;
      case 'hihatOpen':
        this.playHiHat(now, vel, true);
        break;
      case 'clap':
        this.playClap(now, vel);
        break;
      case 'crash':
      case 'ride':
        this.playCymbal(now, vel, drumName === 'crash');
        break;
      default:
        this.playTom(now, vel, DRUM_KIT[drumName]?.note || 60);
    }
  }

  playKick(time, velocity) {
    const ctx = this.audioContext;
    
    // Oscillator for body
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.frequency.setValueAtTime(150, time);
    osc.frequency.exponentialRampToValueAtTime(40, time + 0.1);
    
    gain.gain.setValueAtTime(velocity, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.3);
    
    osc.connect(gain);
    gain.connect(this.destination);
    
    osc.start(time);
    osc.stop(time + 0.3);
  }

  playSnare(time, velocity) {
    const ctx = this.audioContext;
    
    // Noise for snare rattle
    const bufferSize = ctx.sampleRate * 0.2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(velocity * 0.5, time);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, time + 0.15);
    
    // Oscillator for body
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    
    osc.frequency.setValueAtTime(200, time);
    osc.frequency.exponentialRampToValueAtTime(100, time + 0.05);
    
    oscGain.gain.setValueAtTime(velocity * 0.7, time);
    oscGain.gain.exponentialRampToValueAtTime(0.01, time + 0.1);
    
    noise.connect(noiseGain);
    noiseGain.connect(this.destination);
    
    osc.connect(oscGain);
    oscGain.connect(this.destination);
    
    noise.start(time);
    noise.stop(time + 0.2);
    osc.start(time);
    osc.stop(time + 0.1);
  }

  playHiHat(time, velocity, open = false) {
    const ctx = this.audioContext;
    
    const bufferSize = ctx.sampleRate * (open ? 0.3 : 0.1);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    
    // High-pass filter for metallic sound
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 7000;
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(velocity * 0.3, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + (open ? 0.3 : 0.08));
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.destination);
    
    noise.start(time);
    noise.stop(time + (open ? 0.35 : 0.1));
  }

  playClap(time, velocity) {
    const ctx = this.audioContext;
    
    // Multiple short noise bursts
    for (let i = 0; i < 3; i++) {
      const bufferSize = ctx.sampleRate * 0.02;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let j = 0; j < bufferSize; j++) {
        data[j] = Math.random() * 2 - 1;
      }
      
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 2000;
      
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(velocity * 0.4, time + i * 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, time + i * 0.01 + 0.1);
      
      noise.connect(filter);
      filter.connect(gain);
      gain.connect(this.destination);
      
      noise.start(time + i * 0.01);
      noise.stop(time + i * 0.01 + 0.1);
    }
  }

  playCymbal(time, velocity, crash = true) {
    const ctx = this.audioContext;
    
    const duration = crash ? 1.5 : 0.8;
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 5000;
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(velocity * 0.4, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + duration);
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.destination);
    
    noise.start(time);
    noise.stop(time + duration + 0.1);
  }

  playTom(time, velocity, note) {
    const ctx = this.audioContext;
    const freq = midiToFrequency(note);
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.frequency.setValueAtTime(freq * 1.5, time);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.5, time + 0.2);
    
    gain.gain.setValueAtTime(velocity, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.3);
    
    osc.connect(gain);
    gain.connect(this.destination);
    
    osc.start(time);
    osc.stop(time + 0.35);
  }
}

/**
 * Create an empty instrument track
 */
export const createInstrumentTrack = (id, name, instrumentType = 'synth') => ({
  id,
  name: name || `Instrument ${id + 1}`,
  type: 'instrument', // Distinguishes from audio tracks
  instrumentType, // 'synth' or 'drums'
  midiEvents: [], // Array of MIDI events
  volume: 0.8,
  pan: 0,
  muted: false,
  solo: false,
  eq: { bass: 0, mid: 0, treble: 0 },
  effects: { reverb: 0, delay: 0 },
  color: `from-purple-${400 + (id % 3) * 100} to-violet-${500 + (id % 3) * 100}`,
  startTime: 0,
  duration: 0,
  // Synth settings
  synthSettings: {
    waveform: 'sawtooth', // sine, square, sawtooth, triangle
    attack: 0.01,
    decay: 0.1,
    sustain: 0.7,
    release: 0.2,
  },
  // Drum settings
  drumKit: 'standard',
});

export default {
  MIDIEventType,
  createMIDIEvent,
  midiToFrequency,
  midiToNoteName,
  NOTE_NAMES,
  DRUM_KIT,
  PAD_LAYOUT,
  KEYBOARD_MAP,
  SimpleSynth,
  DrumSynth,
  createInstrumentTrack,
};
